<script setup lang="ts">
import {inject, ref, watch} from "vue";
import {Trash2} from "@lucide/vue";
import {supportProgramApi} from "@/container";
import {ProductRecommendation} from "@/core/interfaces/Product";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardHeader from "@/core/components/base/BaseCardHeader.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseImage from "@/core/components/base/BaseImage.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import {Badge} from "@/core/components/ui/badge";
import {price as formatPrice} from "@/core/helpers/formatHelper";
import SupportProgramAddProductRecommendationButton
    from "@/program/components/support-program/SupportProgramAddProductRecommendationButton.vue";

const props = defineProps<{
    supportProgramId: string;
}>();

const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;

const recommendations = ref<ProductRecommendation[]>([]);
const loading = ref(true);

const load = async () => {
    loading.value = true;

    try {
        recommendations.value = await supportProgramApi().productRecommendations(props.supportProgramId);
    } finally {
        loading.value = false;
    }
};

const remove = (recommendation: ProductRecommendation) => {
    openConfirmationModal(
        "Supprimer le produit conseil",
        `Êtes-vous sûr de vouloir supprimer ce produit conseil ?`,
        async () => {
            await supportProgramApi().deleteProductRecommendation(props.supportProgramId, recommendation.id);
            await load();
        }
    );
};

watch(() => props.supportProgramId, load, {immediate: true});
</script>

<template>
  <BaseContainer class="p-6">
    <BaseCard>
      <BaseCardHeader>
        <div class="flex flex-1 items-center justify-between">
          Produits conseils
          <SupportProgramAddProductRecommendationButton
            :support-program-id="supportProgramId"
            @created="load"
          />
        </div>
      </BaseCardHeader>
      <BaseCardBody>
        <BaseFlexSpinner v-if="loading" />
        <div v-else-if="recommendations.length" class="flex flex-col gap-3">
          <div
            v-for="recommendation in recommendations"
            :key="recommendation.id"
            class="flex rounded bg-white border p-3 gap-4 min-h-24"
          >
            <div class="flex flex-col w-20 gap-2">
              <BaseImage
                v-if="recommendation.product.image_url"
                :path="recommendation.product.image_url"
                :text="recommendation.product.name"
                class="w-full h-20 object-fit rounded"
              />
              <div v-else class="bg-gray-100 w-full h-20 rounded" />
              <div class="flex justify-center text-sm">
                {{ (recommendation.price ?? 0) > 0 ? formatPrice(recommendation.price ?? 0) : ' - ' }}
              </div>
            </div>
            <div class="flex-1 flex flex-col gap-1">
              <div class="flex items-center gap-2">
                <p>{{ recommendation.title || recommendation.product.name }}</p>
                <Badge :variant="recommendation.organization_id ? 'primary' : 'secondary'">
                  {{ recommendation.organization_id ? 'Ajouté par vous' : 'Programme' }}
                </Badge>
              </div>
              <MarkdownView
                v-if="recommendation.description"
                :content="recommendation.description"
                class="text-sm leading-tight px-0 py-1"
              />
              <div class="text-gray-600 text-sm flex flex-col gap-1">
                <p v-if="recommendation.posology">
                  {{ recommendation.posology }}
                </p>
                <p v-if="recommendation.comments">
                  {{ recommendation.comments }}
                </p>
                <p>
                  <span :class="(recommendation.stock ?? 0) > 0 ? '' : 'text-red-500'">
                    {{ recommendation.stock ?? 0 }}
                  </span>
                  en stock
                </p>
              </div>
            </div>
            <div v-if="recommendation.organization_id" class="flex items-center">
              <BaseButton
                v-tooltip="'Supprimer le produit conseil'"
                primary
                icon
                density="compact"
                @click="remove(recommendation)"
              >
                <Trash2 class="w-4 h-4" />
              </BaseButton>
            </div>
          </div>
        </div>
        <div v-else class="text-gray-500 text-sm py-6 text-center">
          Personnalisez le module en ajoutant vos produits conseil.
          Ils apparaîtront au clic sur la popup, afin d’aider vos équipes à recommander la solution adaptée au patient ciblé au comptoir.
        </div>
      </BaseCardBody>
    </BaseCard>
  </BaseContainer>
</template>
